import type {
  CreateStaffRequest,
  StaffDetail,
  StaffListItem,
  StaffProductFlags,
  UpdateStaffProductFlagsRequest,
} from '@/definitions/staff'
import api from '@/services/api'

/**
 * Every staff member the organization has.
 *
 * The response carries name, email, username, role, and account state, so a
 * list renders from this alone — `FetchStaffDetail` is only needed for the fields
 * behind a drill-in.
 */
export const FetchStaff = async (orgId: string): Promise<StaffListItem[]> => {
  const response = await api.get<StaffListItem[]>(`/api/v1/staff/${orgId}`)
  return response.data
}

export const FetchStaffDetail = async (
  orgId: string,
  staffId: string
): Promise<StaffDetail> => {
  const response = await api.get<StaffDetail>(`/api/v1/staff/${orgId}/${staffId}`)
  return response.data
}

export const CreateStaff = async (
  orgId: string,
  data: CreateStaffRequest
): Promise<StaffDetail> => {
  const response = await api.post<StaffDetail>(`/api/v1/staff/${orgId}`, data)
  return response.data
}

/**
 * Give a staff member their Barti access back.
 *
 * The returned staff member is what says the account's resulting state; a refusal
 * changes nothing.
 */
export const EnableStaff = async (
  orgId: string,
  staffId: string
): Promise<StaffDetail> => {
  const response = await api.post<StaffDetail>(
    `/api/v1/staff/${orgId}/${staffId}/enable`
  )
  return response.data
}

/** Take a staff member's Barti access away, login included. */
export const DisableStaff = async (
  orgId: string,
  staffId: string
): Promise<StaffDetail> => {
  const response = await api.post<StaffDetail>(
    `/api/v1/staff/${orgId}/${staffId}/disable`
  )
  return response.data
}

/**
 * Sets one staff member's product flags.
 *
 * Turning E-Prescribe on creates their Dosespot clinician under the
 * organization's clinic, which requires the organization's own E-Prescribe to be
 * on already; turning it off deactivates that clinician.
 *
 * The response reports what was actually stored, which is not always what was
 * asked for: the Dosespot call can fail while the request succeeds, and the reason
 * comes back in `integration_errors`.
 */
export const UpdateStaffProductFlags = async (
  orgId: string,
  staffId: string,
  data: UpdateStaffProductFlagsRequest
): Promise<StaffProductFlags> => {
  const response = await api.put<StaffProductFlags>(
    `/api/v1/staff/${orgId}/${staffId}/product-flags`,
    data
  )
  return response.data
}
